// Reading-session tracker (CONTRACTS §2 — POST /view, §4 — ViewStats).
//
// Measures ACTIVE reading time, not wall-clock: the clock only runs while the
// tab is visible AND the reader interacted within IDLE_MS. A doc left open on a
// second monitor therefore stops accruing time instead of quietly logging hours.
//
// Every beat reports the session's ABSOLUTE accumulated ms, never a delta, so a
// retried or duplicated beat is a server-side no-op (recordView derives the
// delta itself). A dropped final beacon costs at most BEAT_MS of recorded time.
//
// The module owns the clock; the viewer just calls markActive() from the
// interaction listeners it already attaches to the iframe document.

const BEAT_MS = 60_000; // heartbeat cadence while reading
const IDLE_MS = 5 * 60_000; // no interaction for this long ⇒ stop the clock
// Grace period before a session is registered at all. Opening the wrong doc and
// closing it straight away is not a "view", so the count means something. It
// also makes the tracker inert under React StrictMode's mount/unmount/remount
// probe in development, which would otherwise log two views per open.
const OPEN_DELAY_MS = 2_000;

export interface ViewTracker {
  /** Note user interaction (pointer/key/scroll), in the parent or the iframe. */
  markActive: () => void;
  /** Flush a final beat and detach. Safe to call more than once. */
  stop: () => void;
}

/** Post a beat. Uses sendBeacon when the page is going away (fetch gets killed). */
function beat(
  docId: string,
  versionId: string,
  sessionId: string,
  ms: number,
  final: boolean
): void {
  const payload = JSON.stringify({ docId, versionId, sessionId, ms });

  // sendBeacon survives pagehide, and carries the same-origin __session cookie,
  // so it passes the identity gate exactly like a normal fetch.
  if (final && typeof navigator !== "undefined" && navigator.sendBeacon) {
    try {
      navigator.sendBeacon(
        "/view",
        new Blob([payload], { type: "application/json" })
      );
      return;
    } catch {
      /* fall through to fetch */
    }
  }

  // Stats are best-effort telemetry: a failed beat must never surface to the
  // reader or interrupt the session.
  void fetch("/view", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: payload,
    credentials: "same-origin",
    keepalive: final,
  }).catch(() => {});
}

/**
 * Begin tracking a reading session for {docId, versionId}. Returns a handle the
 * caller must stop() on unmount / version change. No-ops (returns an inert
 * handle) outside a browser.
 */
export function trackView(docId: string, versionId: string): ViewTracker {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return { markActive: () => {}, stop: () => {} };
  }

  const sessionId =
    crypto.randomUUID?.() ??
    // Older Safari lacks randomUUID; any opaque id matching SESSION_ID_RE works.
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

  let accruedMs = 0; // active time banked from completed intervals
  let runningSince: number | null = null; // start of the interval in flight
  let lastActivity = Date.now();
  let stopped = false;

  const isVisible = () => document.visibilityState === "visible";
  const isIdle = () => Date.now() - lastActivity > IDLE_MS;

  /** Bank the interval in flight (if any) and return total active ms. */
  const settle = (): number => {
    if (runningSince != null) {
      accruedMs += Date.now() - runningSince;
      runningSince = null;
    }
    return accruedMs;
  };

  /** Start the clock if we should be counting, stop it if we shouldn't. */
  const sync = () => {
    const shouldRun = isVisible() && !isIdle();
    if (shouldRun && runningSince == null) runningSince = Date.now();
    else if (!shouldRun && runningSince != null) settle();
  };

  const markActive = () => {
    lastActivity = Date.now();
    sync();
  };

  // The session exists server-side only once the opening beat lands. Until then
  // there is nothing to flush, and stopping must stay completely silent.
  let registered = false;
  sync();
  const openTimer = window.setTimeout(() => {
    registered = true;
    // Registers the session (and so the +1 view) well before the first
    // heartbeat, so a reader who leaves after 30s is still counted.
    beat(docId, versionId, sessionId, 0, false);
  }, OPEN_DELAY_MS);

  const timer = window.setInterval(() => {
    if (!registered) return;
    sync();
    const ms = runningSince != null ? accruedMs + (Date.now() - runningSince) : accruedMs;
    beat(docId, versionId, sessionId, ms, false);
  }, BEAT_MS);

  const onVisibility = () => {
    sync();
    // Going hidden may precede the tab being discarded — flush what we have.
    if (!isVisible() && registered) {
      beat(docId, versionId, sessionId, settle(), true);
    }
  };
  const onPageHide = () => {
    if (stopped || !registered) return;
    beat(docId, versionId, sessionId, settle(), true);
  };

  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("pagehide", onPageHide);

  return {
    markActive,
    stop() {
      if (stopped) return;
      stopped = true;
      window.clearTimeout(openTimer);
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      // Never registered ⇒ the session does not exist server-side. Beating now
      // would create it, turning a two-second glance into a recorded view.
      if (registered) beat(docId, versionId, sessionId, settle(), true);
    },
  };
}
